import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ImageEditor from '../ImageEditor/ImageEditor';
import { ImageData } from '../../../../model/FestivalData';
import { fetchAdvertisements, updateAdvertisement, deleteAdvertisement } from '../../../../services/api';
import { FiAlertTriangle, FiExternalLink, FiImage, FiPlus } from 'react-icons/fi';
import Modal from '../../../../components/Modal';
import PageHeader from '../../../../components/PageHeader';
import './AdvertisementsEditor.css';
import { Advertisement } from '../../../../model/Advertisement';
import { getImageUrl, ImageService } from '../../../../services/imageService';
import { currentMessages, useLanguage, useMessages } from '../../../../i18n/I18n';
import LoadingPlaceholder from '../../../../components/LoadingPlaceholder';
import { loadTabData } from '../../../../services/tabData';

interface AdvertisementsEditorProps {
  sessionToken: string;
  festivalId: string;
  getApiImageUrl: (identifier: string) => string;
}

const URL_PATTERN = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

const AdvertisementsEditor: React.FC<AdvertisementsEditorProps> = ({
  sessionToken,
  festivalId,
  getApiImageUrl,
}) => {
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [editingAd, setEditingAd] = useState<Advertisement | null>(null);
  /** The ad being added: listed and counted while its panel is open, gone if the panel closes without saving */
  const [unsavedAdId, setUnsavedAdId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const t = useMessages('ads');
  const common = useMessages('common');
  const { locale } = useLanguage();
  // Weights and chances written the language's way (0.25 or 0,25), with a fixed number of decimals when given
  const formatNumber = (value: number, decimals?: number) =>
    value.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals ?? 20, useGrouping: false });

  const imageService = new ImageService(sessionToken, festivalId);

  // Calculate total weight and display chances
  const { totalWeight, displayChances, noDisplayChance } = useMemo(() => {
    const total = advertisements.reduce((sum, ad) => sum + ad.weight, 0);
    const chances = new Map<string, number>();
    
    advertisements.forEach(ad => {
      chances.set(ad.identifier, ad.weight * 100);
    });

    // Calculate chance of no ad being displayed
    const noDisplayChance = Math.max(0, (1 - total) * 100);

    return { totalWeight: total, displayChances: chances, noDisplayChance };
  }, [advertisements]);

  const chanceOf = (identifier: string) => {
    const chance = displayChances.get(identifier);
    return chance === undefined ? undefined : formatNumber(chance, 1);
  };

  useEffect(() => {
    const loadAdvertisements = async () => {
      try {
        const data = await loadTabData(festivalId, 'advertisements', () => fetchAdvertisements(sessionToken, festivalId));
        setAdvertisements(data);
      } catch (error) {
        console.error('Failed to load advertisements:', error);
        setError(currentMessages('ads').loadFailed);
      } finally {
        setLoading(false);
      }
    };
    loadAdvertisements();
  }, [sessionToken, festivalId]);

  const handleAddAdvertisement = useCallback(() => {
    const newAd: Advertisement = {
      identifier: uuidv4(),
      title: '',
      description: '',
      image: null,
      background_image: null,
      weight: 0.5,
      link: null,
    };
    setAdvertisements(prev => [...prev, newAd]);
    setUnsavedAdId(newAd.identifier);
    setEditingAd(newAd);
  }, []);

  const closeAd = () => {
    if (unsavedAdId) setAdvertisements(prev => prev.filter(ad => ad.identifier !== unsavedAdId));
    setUnsavedAdId(null);
    setEditingAd(null);
  };

  const validateLink = (link: string | null): boolean => {
    if (!link) return true; // Empty or null link is valid
    return URL_PATTERN.test(link);
  };

  const handleSaveAdvertisement = async (ad: Advertisement) => {
    if (!validateLink(ad.link)) {
      setError(t.invalidUrl);
      return;
    }

    try {
      setSavingId(ad.identifier);
      setError(null);

      // Handle image uploads before saving
      let adToSave = { ...ad };

      // Upload main image if needed
      if (ad.image) {
        const uploadedImage = await imageService.uploadImageData(ad.image);
        adToSave = {
          ...adToSave,
          image: uploadedImage
        };
      }

      // Upload background image if needed
      if (ad.background_image) {
        const uploadedBackgroundImage = await imageService.uploadImageData(ad.background_image);
        adToSave = {
          ...adToSave,
          background_image: uploadedBackgroundImage
        };
      }

      // Save the advertisement with uploaded images
      await updateAdvertisement(sessionToken, festivalId, adToSave);
      
      setAdvertisements(prev =>
        prev.map(item => (item.identifier === adToSave.identifier ? adToSave : item))
      );
      setUnsavedAdId(null);
      setEditingAd(null);
    } catch (error) {
      console.error('Failed to save advertisement:', error);
      setError(error instanceof Error ? error.message : t.saveFailed);
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteAdvertisement = async (identifier: string) => {
    try {
      setDeletingId(identifier);
      await deleteAdvertisement(sessionToken, festivalId, identifier);
      setAdvertisements(prev => prev.filter(ad => ad.identifier !== identifier));
      setEditingAd(null);
    } catch (error) {
      console.error('Failed to delete advertisement:', error);
      setError(error instanceof Error ? error.message : t.deleteFailed);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="advertisements-editor">
        <PageHeader title={t.title} />
        <LoadingPlaceholder layout="cards" count={3} />
      </div>
    );
  }

  const errorMessage = error && <div className="ads-error" role="alert">{error}</div>;

  return (
    <div className="advertisements-editor">
      <PageHeader title={t.title} count={advertisements.length}>
        <button type="button" onClick={handleAddAdvertisement} className="btn btn-primary">
          <FiPlus aria-hidden="true" />
          {t.add}
        </button>
      </PageHeader>

      {totalWeight > 1 && (
        <div className="ads-warning" role="status">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <strong>{t.overweightTitle}</strong>
            <p>{t.overweightText}</p>
          </div>
        </div>
      )}

      <section className="ads-summary" aria-labelledby="ads-summary-title">
        <div className="ads-summary-head">
          <h3 id="ads-summary-title">{t.chancesTitle}</h3>
          <p className={`ads-total${totalWeight > 1 ? ' over' : ''}`}>
            {t.totalWeight} <strong>{formatNumber(totalWeight, 2)}</strong>
          </p>
        </div>
        {advertisements.length === 0 ? (
          <p className="ads-summary-empty">{t.chancesEmpty}</p>
        ) : (
          <>
            {/* Overflowing weights get cut off at the end, like in the app */}
            <div className="ads-bar" aria-hidden="true">
              {Array.from(displayChances.entries()).map(([id, chance]) => (
                <span
                  key={id}
                  className="ads-bar-segment"
                  style={{ flexBasis: `${chance}%` }}
                  title={t.chanceTooltip(advertisements.find(a => a.identifier === id)?.title || t.untitled, formatNumber(chance, 1))}
                />
              ))}
              {totalWeight <= 1 && (
                <span
                  className="ads-bar-segment none"
                  style={{ flexBasis: `${noDisplayChance}%` }}
                  title={t.chanceTooltip(t.noAd, formatNumber(noDisplayChance, 1))}
                />
              )}
            </div>
            <ul className="ads-chances">
              {Array.from(displayChances.entries()).map(([id, chance]) => {
                const ad = advertisements.find(a => a.identifier === id);
                return (
                  <li key={id} className="ads-chance">
                    <span className="ads-chance-dot" aria-hidden="true" />
                    <span className="ads-chance-title">{ad?.title || t.untitled}</span>
                    <span className="ads-chance-value">{t.percent(formatNumber(chance, 1))}</span>
                  </li>
                );
              })}
              {totalWeight <= 1 && (
                <li className="ads-chance none">
                  <span className="ads-chance-dot" aria-hidden="true" />
                  <span className="ads-chance-title">{t.noAd}</span>
                  <span className="ads-chance-value">{t.percent(formatNumber(noDisplayChance, 1))}</span>
                </li>
              )}
            </ul>
          </>
        )}
      </section>

      {!editingAd && errorMessage}
      <ul className="ads-list">
        {advertisements.map(ad => (
          <li key={ad.identifier} className="ads-card">
            {ad.image ? (
              <img src={getImageUrl(ad.image, getApiImageUrl)} alt="" className="ads-card-image" />
            ) : (
              <span className="ads-card-image">
                <FiImage aria-hidden="true" />
              </span>
            )}
            <div className="ads-card-text">
              <h3 className="ads-card-title">
                <button
                  type="button"
                  className="ads-card-open"
                  onClick={() => (editingAd?.identifier === ad.identifier ? closeAd() : setEditingAd(ad))}
                >
                  {ad.title || t.untitledAd}
                </button>
              </h3>
              <p className="ads-card-description">{ad.description}</p>
              {ad.link && (
                <a
                  href={ad.link}
                  className="ads-card-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiExternalLink aria-hidden="true" />
                  <span>{ad.link}</span>
                </a>
              )}
            </div>
            <p className="ads-card-stats">
              <span className="ads-card-weight">{t.weightValue(formatNumber(ad.weight))}</span>{' '}
              <span className="ads-card-chance">
                {t.chanceValue(chanceOf(ad.identifier))}
              </span>
            </p>
          </li>
        ))}
      </ul>

      {editingAd && (
        <Modal
          key={editingAd.identifier}
          isOpen
          onClose={closeAd}
          variant="sheet"
          className="ads-sheet"
        >
          {/* A new ad is listed untitled until it's saved */}
          <h3>{advertisements.find(ad => ad.identifier === editingAd.identifier)?.title ? t.editAd : t.newAd}</h3>
          {errorMessage}
          <div className="ads-field">
            <label htmlFor="ad-title">{t.titleLabel}</label>
            <input
              id="ad-title"
              type="text"
              value={editingAd.title}
              onChange={e => setEditingAd({ ...editingAd, title: e.target.value })}
            />
          </div>
          <div className="ads-field">
            <label htmlFor="ad-description">{t.description}</label>
            <textarea
              id="ad-description"
              rows={3}
              value={editingAd.description}
              onChange={e => setEditingAd({ ...editingAd, description: e.target.value })}
            />
          </div>
          <div className="ads-field">
            <label htmlFor="ad-link">{t.link}</label>
            <p id="ad-link-hint" className="ads-field-hint">{common.optional}</p>
            <input
              id="ad-link"
              type="url"
              aria-describedby="ad-link-hint"
              aria-invalid={linkError ? true : undefined}
              value={editingAd.link || ''}
              onChange={e => {
                const newLink = e.target.value;
                setEditingAd({
                  ...editingAd,
                  link: newLink.trim() === '' ? null : newLink
                });
                setLinkError(validateLink(newLink) ? null : t.invalidUrl);
              }}
              placeholder="https://example.com"
              className={linkError ? 'invalid' : ''}
            />
            {linkError && <p className="ads-field-error">{linkError}</p>}
          </div>
          <div className="ads-field ads-field-weight">
            <label htmlFor="ad-weight">{t.weight}</label>
            <p id="ad-weight-hint" className="ads-field-hint">{t.weightHint}</p>
            <input
              id="ad-weight"
              type="number"
              min="0"
              max="1"
              step="0.1"
              aria-describedby="ad-weight-hint"
              value={editingAd.weight}
              onChange={e => setEditingAd({ ...editingAd, weight: parseFloat(e.target.value) })}
            />
          </div>
          <div className="ads-field ads-image-field">
            <span className="ads-field-label">{t.mainImage}</span>
            <p className="ads-field-hint">{t.mainImageHint}</p>
            <ImageEditor
              currentImage={editingAd.image || undefined}
              onChange={newImage => setEditingAd({ ...editingAd, image: newImage || null })}
              getApiImageUrl={getApiImageUrl}
            />
          </div>
          <div className="ads-field ads-image-field">
            <span className="ads-field-label">{t.backgroundImage}</span>
            <p className="ads-field-hint">{t.backgroundImageHint}</p>
            <ImageEditor
              currentImage={editingAd.background_image || undefined}
              onChange={newImage => setEditingAd({ ...editingAd, background_image: newImage || null })}
              getApiImageUrl={getApiImageUrl}
            />
          </div>
          <div className="modal-actions">
            <button
              type="button"
              onClick={() => handleSaveAdvertisement(editingAd)}
              disabled={savingId === editingAd.identifier}
            >
              {savingId === editingAd.identifier ? t.saving : common.save}
            </button>
            <button type="button" onClick={closeAd}>{common.cancel}</button>
            {editingAd.identifier !== unsavedAdId && (
              <button
                type="button"
                onClick={() => handleDeleteAdvertisement(editingAd.identifier)}
                disabled={deletingId === editingAd.identifier}
                className="danger"
              >
                {deletingId === editingAd.identifier ? t.deleting : common.delete}
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdvertisementsEditor; 